/**
 * Wraps the per-user preferences store (`PREF_LIST_DEFAULT_VIEW`) to resolve,
 * set, and clear a list's default saved view — the mechanism that implements
 * issue #11605 (shipped in PR #11666), NOT a CRM association.
 *
 * `ListDefaultViewPref` (this module's return type), `ListDefaultViewRef`
 * (`useActiveListView`), and `DefaultViewRef` (`SavedViewTabs`) are the
 * identical `{kind, id?}` shape and are passed through interchangeably.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useClearUserPreference,
  useSetUserPreference,
  useUserPreferences,
} from '@/hooks/useUserPreferences';
import {
  ACTIVE_VIEW_PARAM,
  LEGACY_ACTIVE_VIEW_PARAM,
} from '@/lib/crm/hooks/useActiveListView';
import { useSavedFilterViewsList } from '@/lib/crm/list/hooks/useSavedFilterViews';
import type { ListDefaultViewPref } from '@/types/userPreferences';
import { PREF_LIST_DEFAULT_VIEW } from '@/types/userPreferences';

export interface UseListDefaultViewReturn {
  defaultViewRef: ListDefaultViewPref | null;
  /**
   * True while the surface should stay in its loading state because a stored
   * default may still resolve and change which filters apply. Already gated
   * on the deep-link rule: false from the first render when the URL carried a
   * `?view=`/`?savedView=` param at mount (deep links win, so there is nothing
   * to wait for), otherwise true until the prefs + saved-views queries have
   * settled (success OR error — an error must not hang the page; it just
   * falls back to no default).
   */
  isDefaultResolving: boolean;
  setDefaultView: (ref: ListDefaultViewPref) => void;
  clearDefaultView: () => void;
}

export function useListDefaultView(
  orgId: number | undefined,
  scopeKey: string
): UseListDefaultViewReturn {
  const {
    getPreference,
    isSuccess: prefsIsSuccess,
    isError: prefsIsError,
  } = useUserPreferences(orgId ?? 0);
  const setPreference = useSetUserPreference(orgId ?? 0);
  const clearPreference = useClearUserPreference(orgId ?? 0);
  const {
    data: savedViewsForDefault,
    isSuccess: savedViewsIsSuccess,
    isError: savedViewsIsError,
    isFetching: savedViewsIsFetching,
  } = useSavedFilterViewsList(orgId, scopeKey);

  const rawDefaultViewPref = getPreference<ListDefaultViewPref>(
    PREF_LIST_DEFAULT_VIEW,
    scopeKey
  );

  // Only screen against a list that is not mid-refetch. Creating a view
  // invalidates the list and then (via the "Set as default view" checkbox)
  // optimistically writes the new id into the prefs cache; screening against
  // the still-cached pre-create list would judge the brand-new default stale
  // and auto-clear it before the refetch lands.
  const isStaleDefault =
    rawDefaultViewPref?.kind === 'saved' &&
    savedViewsIsSuccess &&
    !savedViewsIsFetching &&
    !(savedViewsForDefault ?? []).some(
      v => v.entity_id === rawDefaultViewPref.id
    );

  useEffect(() => {
    // isStaleDefault itself only flips true->false once the clear lands (row
    // removed), so this fires exactly once per staleness episode. If the
    // clear mutation persistently fails, this effect retries on every
    // re-render — accepted, pre-existing behavior of the shipped entity-list
    // page, intentionally preserved here, not a new defect.
    if (isStaleDefault) {
      clearPreference.mutate({ key: PREF_LIST_DEFAULT_VIEW, scope: scopeKey });
    }
  }, [isStaleDefault, scopeKey, clearPreference]);

  // Must stay memoized — useActiveListView requires a stable defaultView reference.
  const defaultViewRef = useMemo<ListDefaultViewPref | null>(() => {
    if (!prefsIsSuccess || prefsIsError || !savedViewsIsSuccess) {
      return null;
    }
    if (!rawDefaultViewPref) {
      return null;
    }
    if (rawDefaultViewPref.kind === 'saved' && isStaleDefault) {
      return null;
    }
    return rawDefaultViewPref;
  }, [
    prefsIsSuccess,
    prefsIsError,
    savedViewsIsSuccess,
    rawDefaultViewPref,
    isStaleDefault,
  ]);

  // Captured once at mount (like useActiveListView's own
  // mountHadNoStateParamsRef) rather than read fresh every render:
  // useActiveListView's state->URL effect always writes a view token —
  // including "all" — shortly after mount, which would otherwise flip this
  // true before the queries above have any chance to settle, defeating the
  // wait this flag exists to provide. Both the current and legacy param
  // names count as a deep link, matching what useActiveListView seeds from.
  const [searchParams] = useSearchParams();
  const hasUrlViewParamRef = useRef(
    searchParams.has(ACTIVE_VIEW_PARAM) ||
      searchParams.has(LEGACY_ACTIVE_VIEW_PARAM)
  );

  const isDefaultResolving =
    !hasUrlViewParamRef.current &&
    !(
      (prefsIsSuccess || prefsIsError) &&
      (savedViewsIsSuccess || savedViewsIsError)
    );

  // A {kind:'saved'} default is applied asynchronously by the consumer's
  // useSavedViews, whose generation guards make the concurrent stale-clear safe.
  const setDefaultView = useCallback(
    (ref: ListDefaultViewPref) =>
      setPreference.mutate(
        { key: PREF_LIST_DEFAULT_VIEW, scope: scopeKey, value: ref },
        {
          onError: () =>
            toast.error("We couldn't set your default view. Try again."),
        }
      ),
    [setPreference, scopeKey]
  );

  const clearDefaultView = useCallback(
    () =>
      clearPreference.mutate(
        { key: PREF_LIST_DEFAULT_VIEW, scope: scopeKey },
        {
          onError: () =>
            toast.error("We couldn't clear your default view. Try again."),
        }
      ),
    [clearPreference, scopeKey]
  );

  return {
    defaultViewRef,
    isDefaultResolving,
    setDefaultView,
    clearDefaultView,
  };
}
